import * as React from 'react';
import * as mui from '@mui/material';

import useStickyState from 'hooks/useStickyState';
import useDebouncedEffect from 'hooks/useDebouncedEffect';

import { PreviewPageProvider } from 'context/PreviewPageContext';

import BookCarousel from 'components/General/BookCarousel';
import Stepper from './Stepper';
import Step1 from './Steps/Step1';
import Step2 from './Steps/Step2';
import Step3 from './Steps/Step3';

import { makeCartItem, useInventory } from 'models/CartItem';
import { CartItem } from 'models';

const PreviewPage = () => {
	const [inventoryItems, isInventoryLoaded] = useInventory();

	const [activeStep, setActiveStep] = React.useState(1);

	const [selectedStory, setSelectedStory] = useStickyState('story1', 'story'); // Storia selezionata
	const [name1, setName1] = useStickyState('', 'name1'); // Nome personaggio maglia rossa
	const [name2, setName2] = useStickyState('', 'name2'); // Nome personaggio maglia blue
	const [name3, setName3] = useStickyState('', 'name3'); // Nome personaggio maglia gialla
	const [character1, setCharacter1] = useStickyState('', 'character1'); // Primo protagonista
	const [character2, setCharacter2] = useStickyState('', 'character2'); // Primo compagno di viaggio
	const [character3, setCharacter3] = useStickyState('', 'character3'); // Secondo compagno di viaggio
	const [city, setCity] = useStickyState('', 'city'); // Città
	const [dedication1, setDedication1] = useStickyState(
		'Questo viaggio ti porterà in luoghi lontani e meravigliosi. Lasciati guidare sempre dalla curiosità! Buona lettura!',
		'dedication1',
	); // Dedica storia 1
	const [dedication2, setDedication2] = useStickyState(
		'Questo viaggio ti porterà in luoghi lontani e meravigliosi. Lasciati guidare sempre dalla curiosità! Buona lettura!',
		'dedication2',
	); // Dedica storia 2
	const [dedication3, setDedication3] = useStickyState(
		'Questo viaggio ti porterà in luoghi lontani e meravigliosi. Lasciati guidare sempre dalla curiosità! Buona lettura!',
		'dedication3',
	); // Dedica storia 3
	const [dedication4, setDedication4] = useStickyState(
		'Questo viaggio ti porterà in luoghi lontani e meravigliosi. Lasciati guidare sempre dalla curiosità! Buona lettura!',
		'dedication4',
	); // Dedica storia 4

	const [bookVariables, setBookVariables] = React.useState<BookVariables>({
		selectedStory,
		name1,
		name2,
		name3,
		character1,
		character2,
		character3,
		city,
		dedication: { story1: dedication1, story2: dedication2, story3: dedication3, story4: dedication4 }[
			selectedStory as 'story1' | 'story2' | 'story3' | 'story4'
		],
	}); // Oggetto contenente tutte le variabili del libro
	const requiredBookVariables = [selectedStory, name1, name2, name3, character1, character2, character3, city];
	const canShowPreview = !requiredBookVariables.includes('');

	const [errorSnackbarOpen, setErrorSnackbarOpen] = React.useState(false);
	const [waiting, setWaiting] = React.useState(false);

	const [productsChecked, setProductsChecked] = React.useState<(TreePrimaryItemID | TreeSecondaryItemID)[]>([selectedStory as TreePrimaryItemID]);

	React.useEffect(() => {
		const urlParam = new URLSearchParams(window.location.search).get('story');

		if (urlParam) {
			const index = parseInt(urlParam.replace(/\D/g, ''));

			if ([1, 2, 3, 4].includes(index)) {
				setSelectedStory(`story${index}`);
			}
		}
	}, [setSelectedStory]);

	const [cartItems, setCartItems] = React.useState([makeCartItem(selectedStory)]);
	const getCartTotal = (): number => {
		if (cartItems.length === 0) {
			return 0;
		}

		const total = cartItems
			.flatMap(cartItem => {
				const inventoryItem = inventoryItems.find(item => item.name === cartItem.productName);

				if (!inventoryItem) {
					return 0;
				}

				const price = inventoryItem.discountedPrice ?? inventoryItem.price;

				return price * cartItem.quantity;
			})
			.reduce((a, b) => a + b);

		return total;
	};
	const cartTotal: number = getCartTotal();
	const formattedCartTotal: string = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(cartTotal);

	const wordpressData = window['wordpressData' as any] as any;
	const isAdmin = wordpressData !== undefined ? (wordpressData['is_user_admin'] as boolean) : false;

	useDebouncedEffect(
		() =>
			setBookVariables({
				selectedStory,
				name1,
				name2,
				name3,
				character1,
				character2,
				character3,
				city,
				dedication: { story1: dedication1, story2: dedication2, story3: dedication3, story4: dedication4 }[
					selectedStory as 'story1' | 'story2' | 'story3' | 'story4'
				],
			}),
		3000,
		[selectedStory, name1, name2, name3, character1, character3, city, dedication1, dedication2],
	);

	const handleChangeStep = (newStep: number) => {
		const adjNewStep = Math.max(0, Math.min(3, newStep));

		if (validateChangeOfStep(adjNewStep)) {
			setActiveStep(adjNewStep);
		} else {
			setErrorSnackbarOpen(true);
		}
	};

	const handleContinue = () => {
		if (activeStep === 3) {
			handleFinish();
		} else {
			handleChangeStep(activeStep + 1);
		}
	};

	const handleBack = () => {
		handleChangeStep(activeStep - 1);
	};

	React.useEffect(() => {
		// Nel carrello deve rimanere solo la nuova storia selezionata
		setProductsChecked([selectedStory as TreePrimaryItemID]);
	}, [selectedStory]);

	React.useEffect(() => {
		const newCartItems: CartItem[] = productsChecked.map(product => {
			// calendar1_A, calendar1_B, calendar1_C
			if (product.includes('calendar1')) {
				const uniqueCharacterIndex = ['A', 'B', 'C'].indexOf(product.slice(-1).toUpperCase()) + 1;
				return makeCartItem('calendar1', 1, { uniqueCharacterIndex });
			}

			if (product.includes('notebook1')) {
				const uniqueCharacterIndex = ['A', 'B', 'C'].indexOf(product.slice(-1).toUpperCase()) + 1;
				return makeCartItem('notebook1', 1, { uniqueCharacterIndex });
			}

			return makeCartItem(product, 1);
		});

		setCartItems(newCartItems);
	}, [productsChecked]);

	React.useEffect(() => {
		if (activeStep === 2 && canShowPreview) {
			handleChangeStep(3);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canShowPreview]);

	const handleFinish = async () => {
		setWaiting(true);

		const urls = cartItems.flatMap(cartItem => {
			let dedication = '';
			if (cartItem.productName === 'story1' || cartItem.productName === 'audiobook1') {
				dedication = dedication1;
			} else if (cartItem.productName === 'story2' || cartItem.productName === 'audiobook2') {
				dedication = dedication2;
			} else if (cartItem.productName === 'story3' || cartItem.productName === 'audiobook3') {
				dedication = dedication3;
			} else if (cartItem.productName === 'story4' || cartItem.productName === 'audiobook4') {
				dedication = dedication4;
			}
			const [name1Encoded, name2Encoded, name3Encoded, cityEncoded, dedicationEncoded] = [name1, name2, name3, city, dedication].map(
				encodeURIComponent,
			);

			const inventoryItem = inventoryItems.find(item => item.name === cartItem.productName);

			if (!inventoryItem) {
				return [];
			}

			if (cartItem.productName.startsWith('static')) {
				return `?add-to-cart=${inventoryItem.id}`;
			}

			const hasUniqueCharacter = parseInt(cartItem.meta?.uniqueCharacterIndex, 10) >= 1;

			if (hasUniqueCharacter) {
				const uniqueCharacterIndex = cartItem.meta?.uniqueCharacterIndex;

				const uniqueCharacter = [character1, character2, character3][uniqueCharacterIndex - 1];
				const uniqueName = [name1Encoded, name2Encoded, name3Encoded][uniqueCharacterIndex - 1];

				return `?add-to-cart=${inventoryItem.id}&name1=${uniqueName}&name2=${uniqueName}&name3=${uniqueName}&character1=${uniqueCharacter}&character2=${uniqueCharacter}&character3=${uniqueCharacter}&city=${cityEncoded}&dedication=${dedicationEncoded}`;
			}

			return `?add-to-cart=${inventoryItem.id}&name1=${name1Encoded}&name2=${name2Encoded}&name3=${name3Encoded}&character1=${character1}&character2=${character2}&character3=${character3}&city=${cityEncoded}&dedication=${dedicationEncoded}`;
		});

		const requestOptions: RequestInit = {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'same-origin',
			body: JSON.stringify(
				wordpressData
					? {
							is_user_logged_in: wordpressData.is_user_logged_in,
							is_single: wordpressData.is_single,
							postid: wordpressData.postid,
					  }
					: {},
			),
		};

		for (const url of urls) {
			await fetch(url, requestOptions);
		}

		if (!wordpressData) {
			// const text = urls.map(
			// 	(url, index) =>
			// 		`[REQUEST ${index + 1}]: ${url}\n<a href="https://faboo.la${url}" target="_blank" rel="noopener noreferrer">APRI</a>\n`,
			// );
			// document.write(`<pre>${text.join('\n')}</pre>`);
		}

		setWaiting(false);

		await fetch('?wc-ajax=get_refreshed_fragments', requestOptions)
			.then(res => res.json())
			.then(res => {
				const newCount = res['fragments']['.nm-menu-cart-count'];

				Array.from(document.getElementsByClassName('nm-menu-cart-count')).forEach(item => {
					item.outerHTML = newCount;
				});

				window.location.href = '/carrello';
			});
	};

	const validateChangeOfStep = (newStep: number): boolean => {
		if (newStep === 2 && [name1, character1, city].map(e => e.length > 0 && e.length < 20).includes(false)) {
			return false;
		}

		if (newStep === 3 && [name2, character2, name3, character3].map(e => e.length > 0 && e.length < 20).includes(false)) {
			return false;
		}

		return true;
	};

	const providerValue = {
		inventoryItems,
		isInventoryLoaded,
		bookVariables,
		selectedStory,
		name1,
		name2,
		name3,
		character1,
		character2,
		character3,
		city,
		dedication1,
		dedication2,
		dedication3,
		dedication4,
		productsChecked,
		setSelectedStory,
		setName1,
		setName2,
		setName3,
		setCharacter1,
		setCharacter2,
		setCharacter3,
		setCity,
		setDedication1,
		setDedication2,
		setDedication3,
		setDedication4,
		setProductsChecked,
		cartItems,
		setCartItems,
		formattedCartTotal,
		isAdmin,
		handleContinue,
		handleBack,
	};

	return (
		<PreviewPageProvider value={providerValue}>
			<mui.Backdrop style={{ zIndex: 10000 }} open={waiting}>
				<mui.CircularProgress color='inherit' />
			</mui.Backdrop>

			<mui.Typography variant='h5' align='center' color='primary'>
				{canShowPreview ? "Sfoglia l'anteprima gratuita" : "Genera subito l'anteprima gratuita del libro personalizzato!"}
			</mui.Typography>

			{canShowPreview && (
				<mui.Stack justifyContent='center' alignItems='center' sx={{ mt: 4 }}>
					<BookCarousel canShowPreview={canShowPreview} bookVariables={bookVariables} />
				</mui.Stack>
			)}

			<Stepper activeStep={activeStep} setActiveStep={handleChangeStep} formattedCartTotal={formattedCartTotal} />

			<mui.Box sx={{ width: '100%', bgcolor: 'primary.main' }}>
				<mui.Container maxWidth='lg'>
					<mui.Box sx={{ display: 'flex', flexDirection: 'column', py: 5 }}>
						{activeStep === 1 && <Step1 />}
						{activeStep === 2 && <Step2 />}
						{activeStep === 3 && <Step3 />}
					</mui.Box>
				</mui.Container>
			</mui.Box>

			<mui.Snackbar
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				open={errorSnackbarOpen}
				onClose={() => setErrorSnackbarOpen(false)}
				autoHideDuration={3000}
			>
				<mui.Alert severity='error' variant='filled'>
					Attenzione! Hai dimenticato qualcosa. Verifica di aver inserito tutti i dati richiesti per accedere al prossimo passaggio 😉
				</mui.Alert>
			</mui.Snackbar>
		</PreviewPageProvider>
	);
};

export default PreviewPage;
